import { DatePipe } from '@angular/common';
import { Component, HostListener, OnDestroy, computed, inject, signal } from '@angular/core';
import { Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { AuthService } from '../core/auth/auth.service';
import { ThemeService } from '../core/theme/theme.service';
import { NotificationService } from '../core/notification/notification.service';
import { AppNotification, NotificationType } from '../core/notification/notification.models';
import { GlobalSearch } from '../shared/global-search/global-search';
import { ToastHost } from '../shared/toast-host/toast-host';
import { ChangePasswordDialog } from '../shared/change-password-dialog/change-password-dialog';

interface NavItem {
  label: string;
  icon: string;
  path: string;
}

@Component({
  selector: 'app-layout',
  imports: [RouterOutlet, RouterLink, RouterLinkActive, DatePipe, GlobalSearch, ToastHost,
    ChangePasswordDialog],
  templateUrl: './layout.html',
  styleUrl: './layout.scss',
})
export class Layout implements OnDestroy {
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private readonly theme = inject(ThemeService);
  private readonly notificationService = inject(NotificationService);

  /** Estado do tema (escuro), lido pelo ícone do rodapé. */
  protected readonly darkMode = this.theme.dark;

  /** Slug do tenant atual (prefixo das rotas). */
  protected readonly tenant = computed(() => this.auth.currentTenant() ?? '');

  /** Estado do menu lateral no mobile (off-canvas). */
  protected readonly navOpen = signal(false);

  /** Busca global (command palette), aberta pela lupa ou Ctrl/⌘+K. */
  protected readonly searchOpen = signal(false);

  /** Sidebar colapsada (mini, só ícones) — persistido em localStorage. */
  protected readonly collapsed = signal(this.readCollapsed());

  /** Dropdown do utilizador (no rodapé da sidebar). */
  protected readonly userMenuOpen = signal(false);
  /** Diálogo de troca da palavra-passe, aberto pelo menu do utilizador. */
  protected readonly passwordDialogOpen = signal(false);
  protected readonly userName = computed(() => this.auth.tenantSession()?.name ?? 'Utilizador');
  protected readonly userEmail = computed(() => this.auth.tenantSession()?.email ?? '');

  /** Notificações — painel lateral aberto pelo sino do rodapé. */
  protected readonly notifOpen = signal(false);
  protected readonly notifications = signal<AppNotification[]>([]);
  protected readonly unread = signal(0);
  private pollHandle: ReturnType<typeof setInterval> | null = null;

  /** Rodapé da sidebar: versão do sistema e ano corrente. */
  protected readonly version = '1.0.0';
  protected readonly year = new Date().getFullYear();

  /** Casos críticos só aparecem com a integração ligada no tenant e a permissão de leitura. */
  protected readonly mainNav = computed<NavItem[]>(() => [
    { label: 'Início', icon: 'fe-home', path: 'dashboard' },
    { label: 'Ocorrências', icon: 'fe-alert-triangle', path: 'ocorrencias' },
    ...(this.auth.criticalCasesEnabled() && this.auth.can('critical-case:read')
      ? [{ label: 'Casos críticos', icon: 'fe-alert-octagon', path: 'casos-criticos' }]
      : []),
    { label: 'Estatísticas', icon: 'fe-bar-chart-2', path: 'estatisticas' },
    { label: 'Clientes', icon: 'fe-users', path: 'clientes' },
    { label: 'Organizações', icon: 'fe-briefcase', path: 'organizacoes' },
  ]);

  /** Configurações só aparece se o papel gere alguma área de configuração. */
  protected readonly systemNav = computed<NavItem[]>(() =>
    this.auth.can('user:write')
      ? [{ label: 'Configurações', icon: 'fe-settings', path: 'configuracoes' }]
      : []);

  constructor() {
    // Carrega as permissões do utilizador (adaptação de UI por papel) ao entrar na área protegida.
    this.auth.loadPermissions();
    this.refreshUnread();
    // Atualiza o contador de não-lidas periodicamente (badge "ao vivo").
    this.pollHandle = setInterval(() => this.refreshUnread(), 30000);
  }

  ngOnDestroy(): void {
    if (this.pollHandle) {
      clearInterval(this.pollHandle);
    }
  }

  private refreshUnread(): void {
    this.notificationService.unreadCount().subscribe({ next: (r) => this.unread.set(r.count) });
  }

  protected toggleNotif(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    const opening = !this.notifOpen();
    this.notifOpen.set(opening);
    this.userMenuOpen.set(false);
    if (opening) {
      this.notificationService.feed().subscribe({ next: (page) => this.notifications.set(page.content) });
    }
  }

  protected closeNotif(): void {
    this.notifOpen.set(false);
  }

  protected openNotification(n: AppNotification): void {
    this.closeNotif();
    if (!n.read) {
      this.notificationService.markRead(n.id).subscribe({ next: () => this.refreshUnread() });
    }
    if (n.occurrenceId != null) {
      this.router.navigate(['/', this.tenant(), 'ocorrencias', n.occurrenceId]);
    }
  }

  protected markAllNotificationsRead(event: MouseEvent): void {
    event.stopPropagation();
    this.notificationService.markAllRead().subscribe({
      next: () => {
        this.notifications.update((list) => list.map((n) => ({ ...n, read: true })));
        this.unread.set(0);
      },
    });
  }

  protected notifIcon(type: NotificationType): string {
    switch (type) {
      case 'TICKET_REPLY':
        return 'fe-message-square';
      case 'TICKET_STATUS':
        return 'fe-refresh-cw';
      case 'TICKET_ASSIGNED':
        return 'fe-user-check';
      case 'OCCURRENCE_DEADLINE':
        return 'fe-clock';
      case 'OCCURRENCE_NEW':
        return 'fe-inbox';
      case 'OCCURRENCE_STATE':
        return 'fe-shuffle';
    }
  }

  protected toggleNav(): void {
    this.navOpen.update((value) => !value);
  }

  protected closeNav(): void {
    this.navOpen.set(false);
  }

  protected toggleCollapse(): void {
    this.collapsed.update((value) => !value);
    try {
      localStorage.setItem('et-sidebar-collapsed', this.collapsed() ? '1' : '0');
    } catch {
      /* storage indisponível — ignora */
    }
  }

  private readCollapsed(): boolean {
    try {
      return localStorage.getItem('et-sidebar-collapsed') === '1';
    } catch {
      return false;
    }
  }

  protected toggleUser(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.userMenuOpen.update((value) => !value);
  }

  protected toggleDarkMode(): void {
    this.theme.toggle();
  }

  protected openSearch(): void {
    this.searchOpen.set(true);
  }

  protected closeSearch(): void {
    this.searchOpen.set(false);
  }

  /** Atalho de teclado para a busca global (Ctrl/⌘+K) e Esc para fechar o painel lateral. */
  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(event: KeyboardEvent): void {
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
      event.preventDefault();
      this.searchOpen.set(true);
    }
    if (event.key === 'Escape' && this.notifOpen()) {
      this.closeNotif();
    }
  }

  protected openPasswordDialog(event: MouseEvent): void {
    event.preventDefault();
    this.userMenuOpen.set(false);
    this.passwordDialogOpen.set(true);
  }

  protected logout(event: MouseEvent): void {
    event.preventDefault();
    this.auth.logoutTenant();
    this.userMenuOpen.set(false);
    this.router.navigate(['/login']);
  }

  /**
   * Fecha o dropdown do utilizador ao clicar noutro sítio.
   *
   * <p>O painel de notificações não entra aqui: tem fundo próprio, e é esse que o fecha. Se
   * ficasse, o clique que o abre chegava ao documento e fechava-o no mesmo instante.
   */
  @HostListener('document:click')
  onDocumentClick(): void {
    if (this.userMenuOpen()) {
      this.userMenuOpen.set(false);
    }
  }
}
